import { Component, computed, Signal, inject, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TitledContainerComponent } from '@r1-compass-library/components/titled-container/titled-container.component';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { RoutingService } from '@r1-compass-library/services/routing.service';
import { combineLatest, map } from 'rxjs';
import { AppConstants } from '../app.constants';
import { provideComponentStore } from '@ngrx/component-store';
import { PlaysListStore } from './plays-list/plays-list.store';
import { TemplateCriteriaListStore } from '../template-criteria/store/template-criteria-list.store';
import { PlayStore } from './store/play.store';
import { DataMartTablesStore } from '../template-criteria/store/data-mart-tables.store';
import { LifecycleRulesetListStore } from '../lifecycle-rules/store/lifecycle-ruleset-list.store';
import { DynamicContentStore } from './play-talking-points/dynamic-content.store';
import { CompassRouteParent, Play } from '@r1-compass-library/models';
import { DataMartModelsStore } from '@r1-compass-library/store/data-mart-models.store';
import { AdditionalPlayInfoBarComponent } from './additional-info-bar/additional-play-info-bar.component';
import { filterNil } from 'ngxtension/filter-nil';
import { toObservable } from '@angular/core/rxjs-interop';
import { Breadcrumb, buildFolderBreadcrumbs } from '@r1-compass-library/components';
import { PlayValidationContext, PlayValidationService } from './play-validation.service';
import { PlayValidationContextService } from './play-validation-context.service';
import {
    SyncValidationStateService,
    SyncValidationStateTopLevelComponent,
} from '@r1-compass-library/features/validation-sync/sync-validation-state.service';
import { PlaySectionName } from '@r1-compass-library/features/validation-sync/section-names.model';
import { PlayValidationStateService } from './play-validation-state.service';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { rxEffect } from 'ngxtension/rx-effect';
import { ContentAssetSearch } from '@r1-compass-library/models/search/content-asset-search.model';
import { PlayTalkingPointsUsedAssetsStore } from './play-talking-points/play-talking-points.component';

@Component({
    selector: 'r1-compass-app-plays',
    template: `
        @let play = playStore.play();
        @if (showCloseButton() && play) {
            <r1-compass-app-additional-play-info-bar [enabled]="play.enabled" [crumbs]="breadcrumbs()" />
        }
        <r1-compass-lib-titled-container
            (close)="onPlayClosed()"
            [showCloseButton]="showCloseButton()"
            [title]="containerTitle()">
            <router-outlet />
        </r1-compass-lib-titled-container>
    `,
    imports: [TitledContainerComponent, CommonModule, RouterModule, AdditionalPlayInfoBarComponent],
    providers: [
        provideComponentStore(PlaysListStore),
        provideComponentStore(TemplateCriteriaListStore),
        provideComponentStore(PlayStore),
        provideComponentStore(DataMartTablesStore),
        provideComponentStore(LifecycleRulesetListStore),
        provideComponentStore(DynamicContentStore),
        provideComponentStore(DataMartModelsStore),
        PlayValidationService,
        PlayValidationContextService,
        PlayValidationStateService,
        PlayTalkingPointsUsedAssetsStore,
    ],
})
export class PlaysComponent
    implements SyncValidationStateTopLevelComponent<PlaySectionName, Play, PlayValidationContext>
{
    protected readonly routingService = inject(RoutingService);
    readonly #router = inject(Router);
    readonly #route = inject(ActivatedRoute);
    protected readonly playStore = inject(PlayStore);
    protected readonly playsListStore = inject(PlaysListStore);
    readonly #validationService = inject(PlayValidationService);
    readonly #validationContextService = inject(PlayValidationContextService);
    readonly #syncValidationStateService = inject(SyncValidationStateService);
    readonly #validationStateService = inject(PlayValidationStateService);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #lifecycleRulesetListStore = inject(LifecycleRulesetListStore);
    readonly #templateCriteriaListStore = inject(TemplateCriteriaListStore);
    private selectedIamDivision$ = toObservable(this.#globalLoggedInUserStore.selectedIamDivision);
    readonly #dynamicContentStore = inject(DynamicContentStore);
    readonly #playTalkingPointsUsedAssetsStore = inject(PlayTalkingPointsUsedAssetsStore);

    constructor() {
        effect(
            () => {
                const assetId = this.routingService.assetId();
                if (assetId && assetId !== 'new' && !isNaN(Number(assetId))) {
                    const numId = Number(assetId);
                    if (this.playStore.play()?.id !== numId) {
                        this.playStore.getPlay({ id: numId });
                    }
                    this.#validationStateService.resetValidityOfSections(false);
                } else if (assetId === 'new') {
                    if (this.playStore.play() !== undefined) {
                        this.playStore.reset();
                    }
                    this.#validationStateService.resetValidityOfSections(true);
                } else {
                    if (this.playStore.play() !== undefined) {
                        this.playStore.reset();
                    }
                    this.#validationStateService.resetValidityOfSections(false);
                }
            },
            { debugName: 'sync play store with route assetId' }
        );

        effect(
            () => {
                const contentAssetSearch: ContentAssetSearch = { deleted: 'Any' };
                const play = this.playStore.play();
                if (play?.type === 'Importer' && play.id) {
                    contentAssetSearch.playImporterId = play.id;
                }
                untracked(() => {
                    if (play) {
                        this.#dynamicContentStore.searchContentAssets(contentAssetSearch);

                        this.#playTalkingPointsUsedAssetsStore.setUsedContentLibraryIds(
                            new Set(play?.config?.talkingPointsDefinition?.contentAssets.map(el => el.id) ?? [])
                        );
                    }
                });
            },
            { debugName: 'track dynamic content for play validation talking points' }
        );

        effect(
            () => {
                const routeData = this.#route.snapshot.data as CompassRouteParent;
                const invalidSections = this.#validationStateService.invalidSections();

                this.registerInvalidSections(routeData, invalidSections);
            },
            { debugName: 'Play `invalidSections` under given play key' }
        );
        effect(
            () => {
                // Waits until all state the validation depends on has loaded
                const context = this.#validationContextService.context();
                const play = this.playStore.play();
                const assetId = this.routingService.assetId();
                if (context && play && play.id === Number(assetId)) {
                    this.validateSectionsOnLoad(play, this.#validationService, this.#validationStateService, context);
                }
            },
            { debugName: 'Play validation (on load)' }
        );

        rxEffect(this.selectedIamDivision$.pipe(filterNil()), selectedIamDivision => {
            this.#templateCriteriaListStore.getAssets({
                iamDivisionId: selectedIamDivision.id,
            });
            this.#lifecycleRulesetListStore.getAssets({
                iamDivisionId: selectedIamDivision.id,
            });
        });

        rxEffect(
            combineLatest([
                this.playStore.play$.pipe(
                    map(play => play?.folderId),
                    filterNil()
                ),
                this.playsListStore.usersAvailableDivisions$,
                this.playsListStore.selectedIamDivision$.pipe(filterNil()),
            ]).pipe(
                map(([folderId, usersAvailableDivisions, selectedIamDivision]) => ({
                    folderId,
                    usersAvailableDivisions,
                    selectedIamDivision,
                }))
            ),
            res => {
                this.playsListStore.getCurrentFolder(res);
            }
        );
    }

    showCloseButton: Signal<boolean> = computed(() => {
        return this.routingService.parentSegment() !== '';
    });
    breadcrumbs: Signal<Breadcrumb[]> = computed(() => {
        const currentFolder = this.playsListStore.vm().currentFolder;
        return buildFolderBreadcrumbs(currentFolder, '/plays', true);
    });

    containerTitle: Signal<string> = computed(() => {
        if (!this.showCloseButton()) {
            return AppConstants.PlaysComponent.mainTitle;
        }

        const currentChildPath = this.routingService.currentRoutePieces().routeWithoutParams?.split('/').at(-1);
        let subTitle =
            AppConstants.PlaysComponent.children.find(child => child.path === currentChildPath)?.containerTitle ?? '';

        const play = this.playStore.play();
        if (currentChildPath === AppConstants.PlayRecordSelectionComponent.path && play?.type) {
            subTitle = play.type;
        }

        if (this.routingService.assetId() === 'new') {
            return `New Play - ${subTitle}`;
        }

        const entityName = play?.name || 'Play';
        return `${entityName} - ${subTitle}`;
    });

    onPlayClosed() {
        const folderId = this.playStore.play()?.folderId ?? AppConstants.defaultRootFolderId;
        this.playStore.reset();
        this.#router.navigate(['.'], {
            relativeTo: this.#route,
            queryParams: { folderId: folderId },
        });
    }

    registerInvalidSections(routeData: CompassRouteParent, invalidSections: PlaySectionName[]) {
        this.#syncValidationStateService.registerInvalidSections(routeData, invalidSections);
    }

    validateSectionsOnLoad(
        play: Play | undefined,
        validationService: PlayValidationService,
        validationStateService: PlayValidationStateService,
        context: PlayValidationContext
    ) {
        this.#syncValidationStateService.validateSectionsOnLoad(
            play,
            validationService,
            validationStateService,
            context
        );
    }
}
